import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  FaHistory,
  FaSearch,
  FaEye,
  FaEdit,
  FaFileExcel,
} from "react-icons/fa";

// Layout Components
import DepartmentTopbar from "./SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "./SuperAdmin/components/DepertmentSidebar";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";

// Modal Components
import RegisterModal from "./TradeLicense/RegisterModal";
import RenewModal from "./TradeLicense/RenewModal";
import TradeLicenseDetailsModal from "./TradeLicense/TradeLicenseDetailsModal";

function Licence() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [expiryFilter, setExpiryFilter] = useState("all");

  // Role check to determine if the user is a Chairman
  const isChairman = localStorage.getItem("main_role") === "mainchairman";

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = isChairman
    ? localStorage.getItem("adminUser")
      ? JSON.parse(localStorage.getItem("adminUser")).token
      : null
    : localStorage.getItem("token");

  useEffect(() => {
    const fetchTradeLicenses = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/list-trade-license.php`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success && Array.isArray(response.data.data)) {
          setLicenses(response.data.data);
        } else {
          setLicenses([]);
        }
      } catch (error) {
        toast.error("Failed to fetch trade licenses.");
      } finally {
        setLoading(false);
      }
    };
    fetchTradeLicenses();
  }, [API_URL, token]);

  // --- Handlers for Department Actions (will not be available to chairman) ---
  const handleRegisterClick = (row) => {
    setSelectedLicense(row);
    setShowRegisterModal(true);
  };

  const handleRenewClick = (row) => {
    setSelectedLicense(row);
    setShowRenewModal(true);
  };

  const handleEditClick = (row) => {
    navigate(`/departmentDashboard/update-trade-license/${row.id}`);
  };
  // --- End of Department Handlers ---

  // Handlers available for both roles
  const handleHistoryClick = (row) => {
    const path = isChairman
      ? `/trade-license-history/${row.id}`
      : `/trade-license-history/${row.id}`;
    navigate(path);
  };

  const handleDetailsClick = (row) => {
    setSelectedLicense(row);
    setShowDetailsModal(true);
  };

  const isExpired = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00") return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  const filteredLicenses = useMemo(() => {
    return licenses.filter((license) => {
      const matchesSearch = searchTerm
        ? license.trade_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          license.trade_owner.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      let matchesStatus = true;
      if (expiryFilter !== "all") {
        const expired = isExpired(license.expire_date);
        if (expiryFilter === "available") {
          matchesStatus =
            license.registration_status === "Available for registration";
        } else if (expiryFilter === "expired") {
          matchesStatus = expired;
        } else if (expiryFilter === "active") {
          matchesStatus =
            !expired && license.registration_status === "Already registered";
        }
      }
      return matchesSearch && matchesStatus;
    });
  }, [licenses, searchTerm, expiryFilter]);

  const handleExport = () => {
    const formattedData = filteredLicenses.map((license) => ({
      "Trade Name": license.trade_name,
      "Owner Name": license.trade_owner,
      "Expire Date": license.expire_date,
      Status: isExpired(license.expire_date) ? "Expired" : "Active",
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TradeLicenses");
    XLSX.writeFile(workbook, "TradeLicenseReport.xlsx");
  };

  const columns = [
    {
      name: "Reg. No.",
      selector: (row) => row.reg_no || "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: "Trade Name",
      selector: (row) => row.trade_name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Owner Name",
      selector: (row) => row.trade_owner,
      sortable: true,
      grow: 2,
    },
    {
      name: "Expire Date",
      selector: (row) => row.expire_date,
      sortable: true,
      cell: (row) => (
        <span
          className={
            isExpired(row.expire_date) ? "text-danger fw-bold" : "text-success"
          }
        >
          {row.expire_date || "N/A"}
        </span>
      ),
    },
    {
      name: "Status",
      sortable: true,
      cell: (row) => {
        const expired = isExpired(row.expire_date);
        let statusText = row.registration_status;
        let badgeColor = "secondary";
        if (expired) {
          statusText = "Expired";
          badgeColor = "danger";
        } else if (statusText === "Already registered") {
          statusText = "Active";
          badgeColor = "success";
        }
        return <span className={`badge bg-${badgeColor}`}>{statusText}</span>;
      },
    },
    {
      name: "Actions",
      minWidth: isChairman ? "120px" : "240px",
      cell: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => handleDetailsClick(row)}
            title="View Details"
          >
            <FaEye />
          </Button>
          {/* Chairman cannot see other buttons */}
          {!isChairman && (
            <>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={() => handleEditClick(row)}
                title="Edit"
              >
                <FaEdit />
              </Button>
              {row.registration_status === "Available for registration" ? (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleRegisterClick(row)}
                >
                  Register
                </Button>
              ) : (
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => handleRenewClick(row)}
                >
                  Renew
                </Button>
              )}
            </>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleHistoryClick(row)}
            title="View History"
          >
            <FaHistory />
          </Button>
        </div>
      ),
    },
  ];

  const subHeaderComponent = useMemo(
    () => (
      <Row className="g-3 w-100 my-2 align-items-center">
        <Col md={5}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search by Trade or Owner Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={4}>
          <Form.Select
            value={expiryFilter}
            onChange={(e) => setExpiryFilter(e.target.value)}
          >
            <option value="all">Filter by Status: All</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="available">Available for Registration</option>
          </Form.Select>
        </Col>
        <Col md={3} className="text-md-end">
          <Button onClick={handleExport} variant="outline-success" size="sm">
            <FaFileExcel className="me-2" />
            Export
          </Button>
        </Col>
      </Row>
    ),
    [searchTerm, expiryFilter]
  );

  // Choose the layout based on the user's role
  const MainLayout = isChairman
    ? ({ children }) => (
        <>
          <Topbar />
          <div className="d-flex">
            <Sidebar />
            {children}
          </div>
        </>
      )
    : ({ children }) => (
        <>
          <DepartmentTopbar />
          <div className="d-flex">
            <DepartmentSidebar />
            {children}
          </div>
        </>
      );

  return (
    <MainLayout>
      <div className="flex-grow-1 p-3" style={{ background: "#f0f2f5" }}>
        <Card
          className="shadow-sm"
          style={{ border: "none", borderRadius: "18px" }}
        >
          <Card.Header style={{ background: "#fff", padding: "20px" }}>
            <h5 className="m-0 font-weight-bold text-primary">
              {isChairman ? "Trade License Report" : "All Trade Licenses"}
            </h5>
          </Card.Header>
          <Card.Body style={{ padding: "0" }}>
            <DataTable
              columns={columns}
              data={filteredLicenses}
              pagination
              responsive
              progressPending={loading}
              subHeader
              subHeaderComponent={subHeaderComponent}
            />
          </Card.Body>
        </Card>
      </div>

      <TradeLicenseDetailsModal
        show={showDetailsModal}
        handleClose={() => setShowDetailsModal(false)}
        licenseId={selectedLicense ? selectedLicense.id : null}
      />
    </MainLayout>
  );
}

export default Licence;
